import React, { useState, useEffect } from 'react';
import styles from './RolesAccess.module.css';

// Friendly labels for each capability key. Falls back to the raw key if the
// server ever adds a capability the frontend doesn't know yet.
const CAP_LABELS = {
  view:                'View (dashboard, clients, transactions)',
  clients_manage:      'Manage clients & athletes',
  charge:              'Charge sessions',
  credits:             'Load credits',
  transactions_manage: 'Edit / delete transactions',
  media_manage:        'Manage media',
  settings_manage:     'Manage settings (rates, packs, notifications, coach info)',
  integrations_manage: 'Manage Square integration',
  team_manage:         'Manage team (invite admins, roles, owners)',
};
const capLabel = (k) => CAP_LABELS[k] || k;

export default function RolesAccess({ API, authHeader, showToast, currentUser }) {
  const [caps, setCaps]       = useState([]);
  const [roles, setRoles]     = useState([]);
  const [admins, setAdmins]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  // Role edit/add state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData]   = useState({});
  const [showAdd, setShowAdd]     = useState(false);
  const [newRole, setNewRole]     = useState({ name: '', permissions: [] });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [rolesRes, adminsRes] = await Promise.all([
        fetch(`${API}/config/admin-roles`, { headers: authHeader() }).then(r => r.json()),
        fetch(`${API}/admins`, { headers: authHeader() }).then(r => r.json()),
      ]);
      setCaps(Array.isArray(rolesRes.capabilities) ? rolesRes.capabilities : []);
      setRoles(Array.isArray(rolesRes.roles) ? rolesRes.roles : []);
      setAdmins(Array.isArray(adminsRes) ? adminsRes : []);
    } catch {
      showToast('Could not load roles & access', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  // ── Role CRUD (whole-array PUT; server assigns ids to new roles) ─────────────
  const saveRoles = async (nextRoles, successMsg) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/config/admin-roles`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ roles: nextRoles }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Save failed', 'error'); return false; }
      const data = await res.json();
      setRoles(Array.isArray(data.roles) ? data.roles : nextRoles);
      if (successMsg) showToast(successMsg);
      return true;
    } catch { showToast('Network error', 'error'); return false; }
    finally { setSaving(false); }
  };

  const startEdit = (r) => { setEditingId(r.id); setEditData({ ...r, permissions: [...(r.permissions || [])] }); setShowAdd(false); setConfirmDelete(false); };
  const cancelEdit = () => { setEditingId(null); setEditData({}); setConfirmDelete(false); };

  const toggleCap = (data, setData, cap) => {
    const has = (data.permissions || []).includes(cap);
    setData({ ...data, permissions: has ? data.permissions.filter(c => c !== cap) : [...(data.permissions || []), cap] });
  };

  const handleSaveEdit = async () => {
    if (!editData.name?.trim()) { showToast('Role name is required', 'error'); return; }
    const next = roles.map(r => r.id === editingId ? { ...r, name: editData.name.trim(), permissions: editData.permissions || [] } : r);
    if (await saveRoles(next, `${editData.name.trim()} updated`)) cancelEdit();
  };

  const handleAdd = async () => {
    if (!newRole.name.trim()) { showToast('Role name is required', 'error'); return; }
    const next = [...roles, { name: newRole.name.trim(), permissions: newRole.permissions }];
    if (await saveRoles(next, `${newRole.name.trim()} added`)) { setShowAdd(false); setNewRole({ name: '', permissions: [] }); }
  };

  const handleDelete = async () => {
    const target = roles.find(r => r.id === editingId);
    const next = roles.filter(r => r.id !== editingId);
    if (await saveRoles(next, `${target?.name || 'Role'} deleted`)) cancelEdit();
  };

  // ── Admin assignment ─────────────────────────────────────────────────────────
  const assignRole = async (adminId, roleId) => {
    try {
      const res = await fetch(`${API}/admins/${adminId}/role`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ roleId: roleId || null }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Update failed', 'error'); return; }
      setAdmins(prev => prev.map(a => a.id === adminId ? { ...a, roleId: roleId || null } : a));
    } catch { showToast('Network error', 'error'); }
  };

  const toggleOwner = async (admin) => {
    try {
      const res = await fetch(`${API}/admins/${admin.id}/owner`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ isOwner: !admin.isOwner }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); showToast(e.error || 'Update failed', 'error'); return; }
      setAdmins(prev => prev.map(a => a.id === admin.id ? { ...a, isOwner: !admin.isOwner } : a));
      showToast(`${admin.displayName || admin.email} ${!admin.isOwner ? 'is now an owner' : 'is no longer an owner'}`);
    } catch { showToast('Network error', 'error'); }
  };

  const renderCapChecklist = (data, setData) => (
    <div className={styles.capList}>
      {caps.map(cap => (
        <label key={cap} className={styles.capRow}>
          <input
            type="checkbox"
            checked={(data.permissions || []).includes(cap)}
            onChange={() => toggleCap(data, setData, cap)}
          />
          <span>{capLabel(cap)}</span>
        </label>
      ))}
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <h1 className={styles.pageTitle}>Roles &amp; Access</h1>
          <p className={styles.pageSubtitle}>Define roles, assign them to admins, and manage owners.</p>
        </div>
        <button className={styles.addBtn} onClick={() => { setShowAdd(true); setEditingId(null); }}>
          ＋ Add Role
        </button>
      </div>

      {loading ? <p className={styles.muted}>Loading…</p> : (
        <>
          {/* ── Roles ──────────────────────────────────────────────────────── */}
          <div className={styles.sectionLabel}>Roles</div>
          <div className={styles.grid}>
            {roles.map(r => (
              <div key={r.id} className={styles.card}>
                {editingId === r.id ? (
                  <div className={styles.editForm}>
                    <div className={styles.field}>
                      <label className={styles.fieldLabel}>Role Name</label>
                      <input className={styles.fieldInput} value={editData.name || ''}
                        onChange={e => setEditData(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Assistant Coach" />
                    </div>
                    <div className={styles.fieldLabel}>Permissions</div>
                    {renderCapChecklist(editData, setEditData)}
                    <div className={styles.editActions}>
                      <button className={styles.cancelBtn} onClick={cancelEdit}>Cancel</button>
                      <button className={styles.saveBtn} disabled={saving} onClick={handleSaveEdit}>{saving ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                    {!confirmDelete ? (
                      <button className={styles.deleteLink} onClick={() => setConfirmDelete(true)}>Delete Role</button>
                    ) : (
                      <div className={styles.confirmDelete}>
                        <span className={styles.confirmText}>Delete “{editData.name || 'this role'}”? Admins assigned to it lose its access until reassigned.</span>
                        <div className={styles.confirmActions}>
                          <button className={styles.cancelBtn} onClick={() => setConfirmDelete(false)}>Cancel</button>
                          <button className={styles.confirmDeleteBtn} disabled={saving} onClick={handleDelete}>{saving ? 'Deleting...' : 'Confirm Delete'}</button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className={styles.cardLabel}>{r.name}</div>
                    <div className={styles.capChips}>
                      {(r.permissions || []).length === 0
                        ? <span className={styles.noCaps}>No permissions</span>
                        : (r.permissions || []).map(c => <span key={c} className={styles.capChip}>{capLabel(c)}</span>)}
                    </div>
                    <div className={styles.cardActions}>
                      <button className={styles.editBtn} onClick={() => startEdit(r)}>✎ Edit</button>
                    </div>
                  </>
                )}
              </div>
            ))}

            {showAdd && (
              <div className={`${styles.card} ${styles.addCard}`}>
                <div className={styles.addCardTitle}>New Role</div>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Role Name</label>
                  <input className={styles.fieldInput} value={newRole.name}
                    onChange={e => setNewRole(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Front Desk" />
                </div>
                <div className={styles.fieldLabel}>Permissions</div>
                {renderCapChecklist(newRole, setNewRole)}
                <div className={styles.editActions}>
                  <button className={styles.cancelBtn} onClick={() => setShowAdd(false)}>Cancel</button>
                  <button className={styles.saveBtn} disabled={saving} onClick={handleAdd}>{saving ? 'Adding...' : 'Add Role'}</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Admins ─────────────────────────────────────────────────────── */}
          <div className={styles.sectionLabel}>Admins</div>
          <div className={styles.adminCard}>
            {admins.length === 0 ? <div className={styles.muted}>No admins found.</div> : admins.map(a => (
              <div key={a.id} className={styles.adminRow}>
                <div className={styles.adminInfo}>
                  <div className={styles.adminName}>
                    {a.displayName || a.email}
                    {a.id === currentUser?.id && <span className={styles.youTag}>you</span>}
                    {!a.active && <span className={styles.pendingTag}>pending invite</span>}
                  </div>
                  <div className={styles.adminEmail}>{a.email}</div>
                </div>
                <div className={styles.adminControls}>
                  {a.isOwner ? (
                    <span className={styles.ownerBadge}>Owner · full access</span>
                  ) : (
                    <select
                      className={styles.roleSelect}
                      value={a.roleId || ''}
                      onChange={e => assignRole(a.id, e.target.value)}
                    >
                      <option value="">— No role (no access) —</option>
                      {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                  <label className={styles.ownerToggle}>
                    <input type="checkbox" checked={a.isOwner} onChange={() => toggleOwner(a)} />
                    <span>Owner</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <div className={styles.note}>
            <strong>Note:</strong> Owners have full access and are the only ones who can manage the team
            (unless a role is granted “Manage team”). Permission enforcement across the rest of the portal
            turns on in a later step — until then these assignments are saved but not yet enforced.
          </div>
        </>
      )}
    </div>
  );
}
